import _ from 'lodash';
import classnames from 'classnames';
import PropTypes from 'prop-types';
import React from 'react';

import { getAssignee, renderAvatarOrInboxIcon } from './current_assignee';
import Tooltip from 'components/common/tooltip';

export default function ConversationAssignee({
  conversationAssignee,
  className,
  customerAssignee,
  hideTooltips,
  onClick,
  textLabel,
}) {
  if (_.isEmpty(conversationAssignee)) {
    return null;
  }

  const hasCustomerAssignee = !_.isEmpty(customerAssignee);
  const isAssigneeCustomerAssignee = hasSameConversationAndCustomerAssignee();
  const customerAssigneeText = getCustomerAssigneeText(customerAssignee);

  const hideCustomerAssignee = isAssigneeCustomerAssignee || !hasCustomerAssignee;

  return (
    <div
      className={classnames({ customerAssigneeIcon: !hideCustomerAssignee }, className)}
      data-aid="assignment-assignee-display"
      onClick={onClick}
    >
      <CustomerAssigneeIcon
        className={className}
        customerAssignee={customerAssignee}
        customerAssigneeText={customerAssigneeText}
        hasCustomerAssignee={hasCustomerAssignee}
        hideCustomerAssignee={hideCustomerAssignee}
        hideTooltips={hideTooltips}
      />
      <ConversationAssigneeIcon
        className={className}
        conversationAssignee={conversationAssignee}
        customerAssigneeText={customerAssigneeText}
        hideCustomerAssignee={hideCustomerAssignee}
        hideTooltips={hideTooltips}
        isAssigneeCustomerAssignee={isAssigneeCustomerAssignee}
      />

      <div className={`${className}-text`}>
        <div className={`${className}-text-label`}>{isAssigneeCustomerAssignee ? customerAssigneeText : textLabel}</div>
        <div className={`${className}-text-name`}>
          {getAssignee({
            agentName: conversationAssignee.agentName,
            groupName: conversationAssignee.groupName,
            isAssigneeCurrentAgent: conversationAssignee.isAssigneeCurrentAgent,
          })}
        </div>
      </div>
    </div>
  );

  function hasSameConversationAndCustomerAssignee() {
    customerAssignee = customerAssignee || {};
    return (
      customerAssignee.agentId === conversationAssignee.agentId &&
      customerAssignee.groupId === conversationAssignee.groupId
    );
  }
}

ConversationAssignee.propTypes = {
  className: PropTypes.string,
  hideTooltips: PropTypes.bool,
  conversationAssignee: PropTypes.shape({
    agentId: PropTypes.string,
    agentName: PropTypes.string,
    avatarImage: PropTypes.string,
    groupId: PropTypes.string,
    groupName: PropTypes.string,
    isAssigneeCurrentAgent: PropTypes.bool,
  }),
  customerAssignee: PropTypes.shape({
    agentId: PropTypes.string,
    agentName: PropTypes.string,
    avatarImage: PropTypes.string,
    groupId: PropTypes.string,
    groupName: PropTypes.string,
  }),
  onClick: PropTypes.func.isRequired,
  textLabel: PropTypes.string,
};

export function CustomerAssigneeIcon({
  className,
  customerAssignee,
  customerAssigneeText,
  hasCustomerAssignee,
  hideCustomerAssignee,
  hideTooltips,
}) {
  if (hideCustomerAssignee) {
    return null;
  }

  const { agentName, avatarImage, groupName } = customerAssignee;
  const customerAssigneeIconWrapperClassName = classnames({ customerAssignee: hasCustomerAssignee });
  const customerAssigneeClassName = classnames(customerAssigneeIconWrapperClassName, className);
  const children = renderAvatarOrInboxIcon(
    groupName,
    agentName,
    avatarImage,
    customerAssigneeClassName,
    customerAssigneeIconWrapperClassName
  );

  return (
    <Tooltip
      bounds={{ left: 8 }}
      className="customerAssignee"
      message={hideTooltips ? null : getTooltipMessage({ agentName, groupName, label: customerAssigneeText })}
      position="top"
    >
      {children}
    </Tooltip>
  );
}

export function ConversationAssigneeIcon({
  className,
  conversationAssignee,
  customerAssigneeText,
  hideCustomerAssignee,
  hideTooltips,
  isAssigneeCustomerAssignee,
}) {
  const { agentName, groupName, avatarImage } = conversationAssignee;
  const conversationAssigneeIconWrapperClassName = classnames('conversationAssignee', {
    customerAssignee: isAssigneeCustomerAssignee,
  });
  const conversationAssigneeClassName = classnames(conversationAssigneeIconWrapperClassName, className);
  let tooltipMessage = null;
  if (!hideTooltips) {
    tooltipMessage = isAssigneeCustomerAssignee
      ? getTooltipMessage({ agentName, groupName, label: customerAssigneeText })
      : getTooltipMessage({ agentName, groupName, label: 'Current Assignee' });
  }
  const children = renderAvatarOrInboxIcon(
    groupName,
    agentName,
    avatarImage,
    conversationAssigneeClassName,
    conversationAssigneeIconWrapperClassName
  );

  return (
    <Tooltip
      bounds={{ left: 8 }}
      className={classnames('conversationAssignee', { partialConversationAssigneeIcon: !hideCustomerAssignee })}
      message={tooltipMessage}
      position="top"
    >
      {children}
    </Tooltip>
  );
}

export function getTooltipMessage({ agentName, groupName, label }) {
  return (
    <div className="conversationAssignee-tooltip">
      <div className="conversationAssignee-tooltip-label">{label}</div>
      <div className="conversationAssignee-tooltip-text">{agentName ? `${agentName} (${groupName})` : groupName}</div>
    </div>
  );
}

function getCustomerAssigneeText(assignee) {
  assignee = assignee || {};
  return assignee.agentName ? 'Dedicated Hero' : 'Hero Inbox';
}

const assigneePropType = PropTypes.shape({
  agentName: PropTypes.string,
  avatarImage: PropTypes.string,
  groupName: PropTypes.string,
});

CustomerAssigneeIcon.propTypes = {
  className: PropTypes.string,
  customerAssignee: assigneePropType,
  customerAssigneeText: PropTypes.string,
  hasCustomerAssignee: PropTypes.bool,
  hideCustomerAssignee: PropTypes.bool,
  hideTooltips: PropTypes.bool,
};

ConversationAssigneeIcon.propTypes = {
  className: PropTypes.string,
  conversationAssignee: assigneePropType,
  customerAssigneeText: PropTypes.string,
  hideCustomerAssignee: PropTypes.bool,
  hideTooltips: PropTypes.bool,
  isAssigneeCustomerAssignee: PropTypes.bool,
};
